import { PRODUCTS_BY_CATEGORY_ENDPOINT } from "@server/parsers/loop54";
import { KEYS } from "@server/utils/parseLoop/const";
import { parseLoopResponse } from "@server/utils/parseLoop/parseLoopResponse";
import type {
  Loop54Filter,
  Loop54Response,
} from "@server/utils/parseLoop/types";
import { cache } from "react";
import type { Product, Variant } from "types/product";

import { ONCE_PER_EIGHT_HOURS, REVALIDATION_TAGS } from "@/config/cache";
import type { MarketConfiguration } from "@/config/markets/const";
import { setLoopCacheOptions } from "@/utils/cache/setLoopCacheOptions";
import getSearchEngineUser from "@/utils/getSearchEngineUser";

const defaultOptions = {
  method: "POST",
  headers: {
    "Api-Version": "V3",
    "User-Id": getSearchEngineUser(),
  },
};

type Props = {
  endpointHash: string;
  limit: number;
  marketConfig: MarketConfiguration;
  brand: string;
  categories: string[];
  id: string;
};

const fetchCategoryProducts = cache(async (props: Props) => {
  const {
    marketConfig,
    endpointHash,
    limit,
    brand,
    categories: categoryValues,
    id: productId,
  } = props;

  if (!categoryValues?.length) return { items: [] };

  const filter: Loop54Filter = {
    and: [
      {
        type: "type",
        value: "Product",
      },
      {
        not: {
          type: "id",
          value: productId,
        },
      },
      { attributeName: `${marketConfig.loop54Market}_Price` },
      ...(brand
        ? [
            {
              not: {
                type: "attribute" as const,
                attributeName: KEYS.BRAND_KEY_CODE,
                value: brand,
              },
            },
          ]
        : []),
    ],
  };

  const url = `${marketConfig.searchEngineUrl}/${PRODUCTS_BY_CATEGORY_ENDPOINT}#id=${endpointHash}`;

  const fnFetch = async (): Promise<{
    items: (Product | Variant)[];
    category?: string;
  }> => {
    const category = categoryValues?.pop();
    if (!category) return { items: [] };
    const payload = {
      attribute: {
        name: KEYS.CATEGORY_KEY_CODE,
        value: category,
      },
      customData: {
        market: marketConfig.loop54Market,
      },
      resultsOptions: {
        skip: 0,
        take: limit,
        filter,
      },
    };

    const response = await fetch(url, {
      ...defaultOptions,
      body: JSON.stringify(payload),
      ...setLoopCacheOptions({
        revalidate: ONCE_PER_EIGHT_HOURS,
        tags: [
          REVALIDATION_TAGS.loopAll,
          REVALIDATION_TAGS.categoryByCode(category),
          REVALIDATION_TAGS.productById(productId),
        ],
      }),
    });

    if (!response.ok) {
      console.debug("Failed to fetch related category products");
      return { items: [] };
    }
    const data: Loop54Response = await response.json();
    if (data.results.count >= limit) {
      return { items: parseLoopResponse(data, []).items, category };
    }
    if (!categoryValues.length) {
      console.debug("Failed to fetch related category products");
      return { items: [] };
    }
    return fnFetch();
  };

  return fnFetch();
});

export const preload = (props: Props) => {
  void fetchCategoryProducts(props);
};

export default fetchCategoryProducts;
